/**
 * Matter detail: the approval chain as it actually stands, plus the full audit history.
 * Both come straight from Firestore — approvalSteps and auditLog are server-written and
 * client-read-only, so this view can never disagree with the state machine.
 */

import { useState } from 'react';
import { dedupePeople, personLabel } from '../lib/people';
import { useNavigate, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import SharedWithCard from '../components/SharedWithCard';
import StatusBadge from '../components/StatusBadge';
import {
  clonePolicyFieldList,
  stripLifecycleFields,
  stripPreventCloneFields,
} from '../lib/fieldLayout';
import { FORM_SECTIONS } from '../lib/formSchema';
import { canRecallMatter } from '../lib/recall';
import { useCollection, useDoc } from '../lib/useFirestore';
import { formatTs } from './MatterList';
import { displayUserName } from '../lib/workflowTracking';
import { canDownloadMatterRecordPdf, isSharingEnabled } from '../lib/matterRecordPdf';

export default function MatterDetail() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isIntake } = useAuth();
  const { data: matter, loading } = useDoc(`matters/${matterId}`);
  const { data: fieldLayout } = useDoc('settings/fieldLayout');
  const { data: general } = useDoc('settings/general');
  // Hiding the card leaves existing collaborators' access untouched — see isSharingEnabled.
  const sharingEnabled = isSharingEnabled(general);
  const steps = useCollection(`matters/${matterId}/approvalSteps`, [['orderBy', 'order', 'asc']]);
  const audit = useCollection(`matters/${matterId}/auditLog`, [['orderBy', 'at', 'desc']]);
  const roster = useCollection('roster');

  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  if (loading) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;
  if (!matter) return <p className="text-sm text-slate-500 dark:text-slate-400">Matter not found.</p>;

  const names = Object.fromEntries(roster.data.map((u) => [u.id, u.displayName || u.email]));
  const isOwner = matter.createdBy === user.uid;
  const infoRequested = steps.data.find((s) => s.status === 'info_requested');
  const pendingIntakeConflictStep = steps.data.find(
    (s) => s.type === 'intake_conflict_check' && s.status === 'pending'
  );
  // Recall is only offered before the chain starts moving — see lib/recall.js. The
  // callable re-checks server-side, so this is purely about not offering a dead button.
  const canRecall = canRecallMatter({ matter, steps: steps.data, isOwner, isAdmin });
  const canDownloadPdf = canDownloadMatterRecordPdf({ matter, user, isAdmin });

  /**
   * Opens a brand-new, unsubmitted draft pre-filled from this matter. Two separate
   * removals happen first: the fields an admin flagged "never copy" (the same flag the
   * templates feature honours), and the lifecycle keys — numbers, approval state,
   * submission stamps and the attachments array, since those files belong to the matter
   * they were uploaded against, not to the copy.
   */
  const cloneMatter = () => {
    // clonePolicyFieldList, not the effective form list: a hidden/deleted field's saved
    // answers still exist on this matter, and its "never copy" flag must keep working.
    const seedMatter = stripLifecycleFields(
      stripPreventCloneFields(matter, clonePolicyFieldList(FORM_SECTIONS, fieldLayout))
    );
    navigate('/matters/new', {
      state: {
        seedMatter,
        seedLabel: `Cloned from “${matter.client?.name || 'an existing matter'}”.`,
      },
    });
  };

  const call = async (name, payload, key) => {
    setBusy(key);
    setError('');
    try {
      await httpsCallable(functions, name)(payload);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  const downloadMatterRecordPdf = async () => {
    setBusy('download-pdf');
    setError('');
    try {
      const result = await httpsCallable(functions, 'getMatterRecordPdfUrl')({ matterId });
      if (result.data?.available && result.data.url) window.open(result.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4">
      <header className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">
              {matter.client?.name || 'Untitled matter'}
            </h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {matter.filesToBeOpened?.matterName || '—'} · submitted by{' '}
              {displayUserName(names, matter.createdBy)}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <StatusBadge status={matter.status} />
            <button
              type="button"
              onClick={cloneMatter}
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Start a new draft pre-filled from this matter"
            >
              Clone this matter
            </button>
            {canDownloadPdf && (
              <button
                type="button"
                disabled={busy === 'download-pdf'}
                onClick={downloadMatterRecordPdf}
                className="rounded border border-firm-300 dark:border-firm-600 px-3 py-1.5 text-sm text-firm-800 dark:text-firm-100 hover:bg-firm-50 dark:hover:bg-firm-950 disabled:opacity-50"
              >
                {busy === 'download-pdf' ? 'Preparing PDF…' : 'Download matter record PDF'}
              </button>
            )}
            {canRecall && (
              <button
                type="button"
                disabled={busy === 'recall'}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Recall this matter? It leaves the approval workflow and becomes read-only. ' +
                        'Its history is kept — use "Clone this matter" to start a corrected draft. ' +
                        'This cannot be undone.'
                    )
                  )
                    return;
                  call('recallMatter', { matterId }, 'recall');
                }}
                className="rounded border border-amber-400 dark:border-amber-600 px-3 py-1.5 text-sm text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950 disabled:opacity-50"
                title="Pull this matter back out of the approval workflow (no approver has acted yet)"
              >
                {busy === 'recall' ? 'Recalling…' : 'Recall matter'}
              </button>
            )}
          </div>
        </div>
        <dl className="mt-4 flex gap-8 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Client number</dt>
            <dd>{matter.clientNumber || 'Not assigned'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Matter number</dt>
            <dd>{matter.matterNumber || 'Not assigned'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Submitted</dt>
            <dd>{formatTs(matter.submittedAt)}</dd>
          </div>
        </dl>
      </header>

      {error && <p className="rounded bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">{error}</p>}

      {sharingEnabled && (
        <SharedWithCard
          matterId={matterId}
          matter={matter}
          roster={roster.data}
          canEdit={isOwner || isAdmin}
        />
      )}

      {infoRequested && isOwner && (
        <ResubmitPanel
          matterId={matterId}
          step={infoRequested}
          busy={busy === 'resubmit'}
          onSubmit={(note) =>
            call('resubmitStep', { matterId, stepId: infoRequested.id, note }, 'resubmit')
          }
        />
      )}

      {isIntake && pendingIntakeConflictStep && (
        <ConflictCheckResultPanel
          matterId={matterId}
          value={matter.conflictCheck?.hitsFound}
          busy={busy === 'conflict'}
          onSave={(hitsFound) => call('recordConflictCheckResult', { matterId, hitsFound }, 'conflict')}
        />
      )}

      {/* 'submitted' is the one status reserved for a matter submitted while the firm's
          approval workflow was disabled (approval/engine.js's submitWithoutWorkflow) — it
          never has approval steps and never will, so the section is omitted rather than
          shown empty with an "approval" heading. */}
      {matter.status !== 'submitted' && (
      <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Approval chain</h2>
          <ol className="mt-3 divide-y divide-slate-100 dark:divide-slate-800">
          {steps.data.map((step) => (
            <li key={step.id} className="py-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {step.label}
                    {step.kind === 'notify' && (
                      <span className="ml-2 rounded bg-teal-50 dark:bg-teal-950 px-1.5 py-0.5 text-xs font-normal text-teal-700 dark:text-teal-200">
                        notify
                      </span>
                    )}
                    {step.kind === 'data_collection' && (
                      <span className="ml-2 rounded bg-firm-50 dark:bg-firm-900 px-1.5 py-0.5 text-xs font-normal text-firm-700 dark:text-firm-100">
                        data collection{step.blocking === false ? ' — side task' : ''}
                      </span>
                    )}
                    {step.groupIndex != null && (
                      <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
                        group {step.groupIndex}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {(step.eligibleApproverUids || []).map((u) => displayUserName(names, u)).join(' / ') ||
                      'Unresolved — escalated'}
                    {step.satisfyMode === 'all' && ' (all must approve)'}
                  </p>
                  {(step.reasons || []).map((reason, i) => (
                    <p key={i} className="mt-1 text-xs text-firm-700 dark:text-firm-100">
                      · {reason.text}
                    </p>
                  ))}
                  {(step.comments || []).map((c, i) => (
                    <p key={i} className="mt-1 rounded bg-slate-50 dark:bg-slate-800 px-2 py-1 text-xs text-slate-700 dark:text-slate-200">
                      <strong>{displayUserName(names, c.by || c.uid, 'Unknown user', c.byName)}:</strong> {c.text}
                    </p>
                  ))}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <StatusBadge status={step.status} />
                  {step.decidedAt && (
                    <span className="text-xs text-slate-400 dark:text-slate-500">{formatTs(step.decidedAt)}</span>
                  )}
                  {isAdmin && ['pending', 'waiting', 'info_requested'].includes(step.status) && (
                    <AdminStepActions
                      matterId={matterId}
                      step={step}
                      roster={roster.data}
                      busy={busy}
                      call={call}
                    />
                  )}
                </div>
              </div>
            </li>
          ))}
          {steps.data.length === 0 && (
            <li className="py-3 text-sm text-slate-500 dark:text-slate-400">
              No approval steps yet — this matter is still a draft.
            </li>
          )}
        </ol>
      </section>
      )}

      <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">History</h2>
        <ol className="mt-3 space-y-2">
          {audit.data.map((entry) => (
            <li key={entry.id} className="flex gap-3 text-sm">
              <span className="w-40 shrink-0 text-xs text-slate-400 dark:text-slate-500">{formatTs(entry.at)}</span>
              <span className="flex-1">
                <span className="text-slate-800 dark:text-slate-100">{entry.summary}</span>
                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                  {displayUserName(names, entry.actor?.uid, 'system', entry.actor?.displayName)}
                  {entry.actor?.via && ` · via ${entry.actor.via}`}
                </span>
              </span>
            </li>
          ))}
          {audit.data.length === 0 && <li className="text-sm text-slate-500 dark:text-slate-400">Nothing yet.</li>}
        </ol>
      </section>
    </div>
  );
}

function ResubmitPanel({ step, busy, onSubmit }) {
  const [note, setNote] = useState('');
  const lastComment = (step.comments || []).slice(-1)[0];
  return (
    <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-4">
      <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
        More information requested — {step.label}
      </h2>
      {lastComment && <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">“{lastComment.text}”</p>}
      <textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Your response…"
        className="mt-2 w-full rounded border border-amber-300 dark:border-amber-700 px-3 py-2 text-sm"
      />
      <button
        type="button"
        disabled={busy || !note.trim()}
        onClick={() => onSubmit(note)}
        className="mt-2 rounded bg-amber-600 dark:bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
      >
        Send response and re-notify approver
      </button>
    </div>
  );
}

/**
 * Intake owns the factual outcome of the conflicts report. The result is collected after
 * submission because the Intake conflict-check approval step is when the report is run.
 * The callable enforces both the Intake role and that this matter has a pending Intake
 * gate; this panel only exposes that narrowly-scoped action.
 */
function ConflictCheckResultPanel({ matterId, value, busy, onSave }) {
  const [hitsFound, setHitsFound] = useState(value === true ? 'yes' : value === false ? 'no' : '');

  return (
    <section className="rounded-lg border border-firm-200 dark:border-firm-700 bg-firm-50 dark:bg-firm-900 p-4">
      <h2 className="text-sm font-semibold text-firm-700 dark:text-firm-100">Conflict-check result</h2>
      <p className="mt-1 text-sm text-firm-800 dark:text-firm-100">
        Record the result of the Aderant conflict report before completing the Intake approval.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1 text-sm">
          <input type="radio" name="conflict-result" checked={hitsFound === 'yes'} onChange={() => setHitsFound('yes')} />
          Hits found
        </label>
        <label className="flex items-center gap-1 text-sm">
          <input type="radio" name="conflict-result" checked={hitsFound === 'no'} onChange={() => setHitsFound('no')} />
          No hits found
        </label>
        <button
          type="button"
          disabled={busy || !hitsFound}
          onClick={() => onSave(hitsFound === 'yes')}
          className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save result'}
        </button>
      </div>
    </section>
  );
}

/** Admin override powers. Every one of these is audit-logged server-side. */
function AdminStepActions({ matterId, step, roster, busy, call }) {
  const [open, setOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reason, setReason] = useState('');

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-slate-500 dark:text-slate-400 hover:text-firm-700 dark:hover:text-firm-100 hover:underline"
      >
        Admin override
      </button>
    );
  }

  return (
    <div className="w-72 space-y-2 rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-2 text-left">
      <input
        className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs"
        placeholder="Reason (recorded in the audit log)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-1">
        <button
          type="button"
          disabled={busy === step.id || !reason.trim()}
          onClick={() =>
            call('adminOverrideStep', { matterId, stepId: step.id, decision: 'approve', reason }, step.id)
          }
          className="rounded bg-emerald-600 dark:bg-emerald-500 px-2 py-1 text-xs text-white disabled:opacity-40"
        >
          Force approve
        </button>
        <button
          type="button"
          disabled={busy === step.id || !reason.trim()}
          onClick={() =>
            call('adminOverrideStep', { matterId, stepId: step.id, decision: 'skip', reason }, step.id)
          }
          className="rounded bg-slate-600 dark:bg-slate-500 px-2 py-1 text-xs text-white disabled:opacity-40"
        >
          Skip
        </button>
      </div>
      <div className="flex gap-1">
        <select
          className="flex-1 rounded border border-slate-300 dark:border-slate-600 px-1 py-1 text-xs"
          value={reassignTo}
          onChange={(e) => setReassignTo(e.target.value)}
        >
          <option value="">Reassign to…</option>
          {dedupePeople(roster).map((u) => (
            <option key={u.id} value={u.id}>
              {personLabel(u)}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy === step.id || !reassignTo || !reason.trim()}
          onClick={() =>
            call('adminReassignStep', { matterId, stepId: step.id, newApproverUids: [reassignTo], reason }, step.id)
          }
          className="rounded bg-firm-700 dark:bg-firm-500 px-2 py-1 text-xs text-white disabled:opacity-40"
        >
          Go
        </button>
      </div>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-xs text-slate-500 dark:text-slate-400 hover:underline"
      >
        Close
      </button>
    </div>
  );
}
